import { useState, type ReactElement } from 'react'
import { cn } from 'app/lib/utils'
import type { FieldMovement, ModelAnalysis, Tally } from './analysis'
import { formatAccuracy, formatPoints } from './stats'
import type { Stage } from './scoring'

export type FieldFilter = { stage: Stage; field: string; kind: 'fixed' | 'broke' }

const STAGE_NAME: Record<Stage, string> = {
  overview: `Overview`,
  people: `People`,
  appointment: `Appointment`,
  outcome: `Outcome`,
}

const FIELD_NAME: Record<string, string> = {
  peopleCount: `People count`,
  name: `Name`,
  patientType: `Patient type`,
  isCaller: `Is caller`,
  answerer: `Answerer`,
  channel: `Channel`,
  callType: `Call type`,
  appointmentScheduled: `Appointment scheduled`,
  appointmentRequested: `Appointment requested`,
  appointmentDate: `Appointment date`,
  appointmentTime: `Appointment time`,
  outcomes: `Outcomes`,
  relationship: `Relationship`,
  phone: `Phone`,
  email: `Email`,
  birthDate: `Birth date`,
  treatment: `Treatment`,
  serviceRequested: `Service requested`,
  reasonForNotScheduling: `Reason not scheduled`,
  insuranceProvider: `Insurance`,
  referral: `Referral`,
}

const rate = (tally: Tally) => (tally.total ? tally.matched / tally.total : 1)

/** Baseline and candidate as two dots on one 0–100% track: the gap is the change. */
function Dumbbell({ baseline, candidate }: { baseline: number; candidate: number }): ReactElement {
  const low = Math.min(baseline, candidate)
  const high = Math.max(baseline, candidate)
  const tone = candidate > baseline ? `bg-green-600` : candidate < baseline ? `bg-red-600` : `bg-[var(--fg-minimal)]`
  return (
    <div className="relative h-4 w-full min-w-24" aria-hidden>
      <div className="absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-surface-muted" />
      <div
        className={cn(`absolute top-1/2 h-1 -translate-y-1/2 rounded-full`, tone)}
        style={{ left: `${low * 100}%`, width: `${(high - low) * 100}%` }}
      />
      <div
        className="absolute top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full border border-[var(--surface)] bg-[var(--fg-minimal)]"
        style={{ left: `${baseline * 100}%` }}
      />
      <div
        className="absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-[var(--surface)] bg-blue-600"
        style={{ left: `${candidate * 100}%` }}
      />
    </div>
  )
}

function CountButton({
  count,
  kind,
  active,
  onClick,
}: {
  count: number
  kind: 'fixed' | 'broke'
  active: boolean
  onClick: () => void
}): ReactElement {
  if (count === 0) return <span className="text-minimal">–</span>
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      title={`Show the calls where this ${kind === `fixed` ? `was fixed` : `broke`}`}
      className={cn(
        `rounded px-1.5 py-0.5 font-medium tabular-nums underline-offset-2 hover:underline`,
        kind === `fixed` ? `text-green-700` : `text-red-700`,
        active && (kind === `fixed` ? `bg-green-100` : `bg-red-100`),
      )}
    >
      {kind === `fixed` ? `+` : `−`}
      {count}
    </button>
  )
}

function Row({
  label,
  baseline,
  candidate,
  fixed,
  broke,
  items,
  strong,
  onFilter,
  activeKind,
}: {
  label: string
  baseline: Tally
  candidate: Tally
  fixed: number
  broke: number
  items: number
  strong?: boolean
  onFilter?: (kind: 'fixed' | 'broke') => void
  activeKind?: 'fixed' | 'broke'
}): ReactElement {
  const moved = fixed + broke > 0
  return (
    <tr className={cn(`border-b border-subtle last:border-b-0`, !strong && !moved && `text-minimal`)}>
      <th
        scope="row"
        className={cn(`py-2 pl-3 pr-2 text-left font-normal`, strong ? `font-medium text-strong` : `pl-6 text-default`)}
      >
        {label}
      </th>
      <td className="px-2 py-2 text-right tabular-nums">
        {formatAccuracy(rate(baseline), items)}
        <span className="ml-1 text-caption-sm text-minimal">
          {baseline.matched}/{baseline.total}
        </span>
      </td>
      <td className="hidden w-40 px-2 py-2 sm:table-cell">
        <Dumbbell baseline={rate(baseline)} candidate={rate(candidate)} />
      </td>
      <td className="px-2 py-2 text-right tabular-nums">
        <span className={strong ? `font-medium text-strong` : undefined}>{formatAccuracy(rate(candidate), items)}</span>
        <span className="ml-1 text-caption-sm text-minimal">
          {candidate.matched}/{candidate.total}
        </span>
      </td>
      <td
        className={cn(
          `px-2 py-2 text-right tabular-nums`,
          rate(candidate) > rate(baseline)
            ? `text-green-700`
            : rate(candidate) < rate(baseline)
              ? `text-red-700`
              : `text-minimal`,
        )}
      >
        {formatPoints(rate(candidate) - rate(baseline), items)}
      </td>
      <td className="px-2 py-2 text-right">
        {onFilter ? (
          <CountButton count={fixed} kind="fixed" active={activeKind === `fixed`} onClick={() => onFilter(`fixed`)} />
        ) : (
          <span className="tabular-nums text-green-700">{fixed ? `+${fixed}` : `–`}</span>
        )}
      </td>
      <td className="py-2 pl-2 pr-3 text-right">
        {onFilter ? (
          <CountButton count={broke} kind="broke" active={activeKind === `broke`} onClick={() => onFilter(`broke`)} />
        ) : (
          <span className="tabular-nums text-red-700">{broke ? `−${broke}` : `–`}</span>
        )}
      </td>
    </tr>
  )
}

/**
 * Where the prompt change moved the score: each stage baseline → candidate, with its
 * fields underneath. Fixed / broke counts filter the call list to exactly those calls.
 */
export function MovementTable({
  analysis,
  candidateLabel,
  baselineLabel,
  filter,
  onFilter,
}: {
  analysis: ModelAnalysis
  candidateLabel: string
  baselineLabel: string
  filter: FieldFilter | null
  onFilter: (filter: FieldFilter | null) => void
}): ReactElement {
  const [showUnchanged, setShowUnchanged] = useState(false)
  const items = analysis.comparison.items
  const unchanged = analysis.fields.filter((field) => field.fixed + field.broke === 0).length

  const toggle = (field: FieldMovement, kind: 'fixed' | 'broke') =>
    onFilter(
      filter?.stage === field.stage && filter.field === field.field && filter.kind === kind
        ? null
        : { stage: field.stage, field: field.field, kind },
    )

  return (
    <section aria-labelledby="movement-heading">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h4 id="movement-heading" className="text-sm font-semibold text-strong">
          Where it moved
        </h4>
        <p className="text-caption-sm text-minimal">
          Field rows are descriptive: across many fields, one will move by chance.
        </p>
      </div>
      <div className="overflow-x-auto rounded-lg border border-default bg-surface-raised">
        <table className="w-full min-w-[34rem] text-sm">
          <thead>
            <tr className="border-b border-default bg-surface-subtle text-caption-sm text-subtle">
              <th scope="col" className="py-2 pl-3 pr-2 text-left font-medium">
                Stage / field
              </th>
              <th scope="col" className="px-2 py-2 text-right font-medium">
                {baselineLabel}
              </th>
              <th scope="col" className="hidden px-2 py-2 sm:table-cell">
                <span className="sr-only">Change</span>
              </th>
              <th scope="col" className="px-2 py-2 text-right font-medium">
                {candidateLabel}
              </th>
              <th scope="col" className="px-2 py-2 text-right font-medium">
                Δ pts
              </th>
              <th scope="col" className="px-2 py-2 text-right font-medium" title="Baseline wrong, candidate right">
                Fixed
              </th>
              <th scope="col" className="py-2 pl-2 pr-3 text-right font-medium" title="Baseline right, candidate wrong">
                Broke
              </th>
            </tr>
          </thead>
          <tbody>
            {analysis.stages.map((stage) => {
              const fields = analysis.fields.filter(
                (field) => field.stage === stage.stage && (showUnchanged || field.fixed + field.broke > 0),
              )
              const stageFields = analysis.fields.filter((field) => field.stage === stage.stage)
              return [
                <Row
                  key={stage.stage}
                  label={STAGE_NAME[stage.stage]}
                  baseline={stage.baseline}
                  candidate={stage.candidate}
                  fixed={stageFields.reduce((sum, field) => sum + field.fixed, 0)}
                  broke={stageFields.reduce((sum, field) => sum + field.broke, 0)}
                  items={items}
                  strong
                />,
                ...fields.map((field) => (
                  <Row
                    key={`${field.stage}.${field.field}`}
                    label={FIELD_NAME[field.field] ?? field.field}
                    baseline={field.baseline}
                    candidate={field.candidate}
                    fixed={field.fixed}
                    broke={field.broke}
                    items={items}
                    onFilter={(kind) => toggle(field, kind)}
                    activeKind={filter?.stage === field.stage && filter.field === field.field ? filter.kind : undefined}
                  />
                )),
              ]
            })}
          </tbody>
        </table>
      </div>
      {unchanged > 0 && (
        <button
          type="button"
          className="mt-1.5 text-caption-sm text-subtle hover:text-strong"
          onClick={() => setShowUnchanged((value) => !value)}
        >
          {showUnchanged ? `Hide` : `Show`} {unchanged} unchanged {unchanged === 1 ? `field` : `fields`}
        </button>
      )}
    </section>
  )
}

export { FIELD_NAME, STAGE_NAME }
